const HIGHLIGHT_CLASS = 'eag-column-highlight';
const HIGHLIGHT_COLOR = '#B3D4FF';
const FLASH_DURATION_MS = 1500;

// Track active timers so a second call cancels the previous flash.
const activeTimers = new WeakMap<HTMLTableElement, number>();
const activeCells = new WeakMap<HTMLTableElement, HTMLElement[]>();

/**
 * Scrolls the grid so that the given column is visible, then applies a brief
 * background flash highlight across the entire column (header + data cells).
 *
 * Uses inline styles + CSS transition because sticky-positioned headers
 * don't reliably render Web Animations API background changes in Chrome.
 */
export function scrollToColumn(tableEl: HTMLTableElement, columnKey: string): void {
  const escaped = CSS.escape(columnKey);
  const th = tableEl.querySelector<HTMLElement>(`th[data-column="${escaped}"]`);
  if (!th) {
    return;
  }

  th.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});

  // Cancel any previous flash
  const prevTimer = activeTimers.get(tableEl);
  if (prevTimer !== undefined) {
    clearTimeout(prevTimer);
    const prev = activeCells.get(tableEl);
    if (prev) {
      prev.forEach(cleanupFlash);
    }
  }

  // Select all cells in this column (th + td).
  // O(visible rows) DOM reads + writes — fine for typical page sizes (≤200 rows).
  const cells = Array.from(tableEl.querySelectorAll<HTMLElement>(`[data-column="${escaped}"]`));

  // 1. Apply highlight color instantly (no transition).
  //    The CSS class is used by e2e tests to assert highlight presence.
  for (const cell of cells) {
    cell.classList.add(HIGHLIGHT_CLASS);
    cell.style.transition = 'none';
    cell.style.backgroundColor = HIGHLIGHT_COLOR;
  }

  // 2. Force reflow so the browser commits the highlight color before we
  //    start the transition. Without this, the browser batches both the
  //    set and the clear into a single frame and no flash is visible.
  void th.offsetHeight;

  // 3. Fade out over FLASH_DURATION_MS
  for (const cell of cells) {
    cell.style.transition = `background-color ${FLASH_DURATION_MS}ms ease-out`;
    cell.style.backgroundColor = '';
  }

  // 4. Clean up inline styles after the transition ends
  const timer = window.setTimeout(() => {
    cells.forEach(cleanupFlash);
    activeTimers.delete(tableEl);
    activeCells.delete(tableEl);
  }, FLASH_DURATION_MS + 50);

  activeTimers.set(tableEl, timer);
  activeCells.set(tableEl, cells);
}

function cleanupFlash(el: HTMLElement): void {
  el.classList.remove(HIGHLIGHT_CLASS);
  el.style.transition = '';
  el.style.backgroundColor = '';
}
